var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { AskErrorMessage, AskOkMessage, AttachToModelFacet, BroadcastToPeerMessage, ButtonSignal, ButtonType, ControllerButtonMessage, CreateSubModelMessage, DeleteModelMessage, DeleteModelOnDisconnectFacet, DropOffMode, INVALID_UID, LocalTransformFacet, RemoveFacetsOnDisconnectFacet, RemoveFacetFromModelMessage, RemoveSubModelMessage, UpdateFacetOnModelMessage, worldToLocalTransform, LinkToModelFacet, ScriptFacet, ScriptComposeFacet, } from "flavr-common";
import { Matrix4, Quaternion, Vector3 } from "flavr-math";
import { ControllerConnectedMessage, ControllerDisconnectedMessage, ControllerSelectionRayVisibilityMessage, MouseDisconnectedMessage, } from "@messages/controller/ControllerMessages";
import { AttachmentChangedMessage, DropFromControllerMessage, DropModelMessage, OnDropModelMessage, OnPickupModelMessage, PickupModelMessage, PickupModelResponseMessage, RequestCanPickupModelMessage, ResetModelPickupsRequestMessage } from "@messages/attachment/AttachmentMessage";
import { EnableMarkerRadarMessage } from "@messages/rendering";
import { EnableControllerSelectionMessage, EnableModelSelectionMessage } from "@messages/selection";
import { UpdateWorldTransformationMessage } from "@messages/scenegraph/ScenegraphMessages";
import { getModelNameByUID, modelPool, modelToAbsolutePath } from "@modules/model";
import { MessageHandler, MessageRequestHandler, NewModule, PresentationMode } from "@modules/module";
import { networkEntitiesService } from "@modules/network";
import { serverConnection } from "@modules/multiplayer/server/ServerConnectionService";
import { controllerDeviceService, getControllerPickupTargetUid } from "@modules/inputs/controller";
import { DropOffService } from "./dropOff/DropOffService";
import { coreUserService } from "../users";
/// Temporary variables to reduce GC ///
const TEMP_MAT_1 = new Matrix4();
/**
 * Handles picking models up, attaching them to controllers and dropping them again so grab interactions can move scene content without each feature reimplementing attachment logic.
 *
 * Messages:
 * - `PickupModelMessage` and `RequestCanPickupModelMessage` - Validate whether a model can be picked up and start a pickup operation for the requested controller.
 * - `DropModelMessage`, `DropFromControllerMessage`, `ControllerButtonMessage`, `ControllerDisconnectedMessage` and `MouseDisconnectedMessage` - End pickups explicitly or in reaction to input and device disconnects.
 * - `RemoveSubModelMessage` and `DeleteModelMessage` - Clean up pickup state when a picked up model disappears from the scene.
 */
export class ModelPickupModule extends NewModule {
    // Drop off areas
    /**
     * Creates the `DropOffService` used to resolve valid drop-off targets for picked up models.
     */
    constructor() {
        super("ModelPickupModule");
        this.customControllers = new Map();
        this.controllerMetaDataMap = new Map();
        this.controllerPickups = new Map();
        this.pickedUpModels = new Map();
        /** Stores the world-space orientation each model had before its very first pickup. Persists across drop/re-pickup cycles. */
        this.initialOrientationByModelUID = new Map();
        /** Which hand each controller UID we've seen belongs to, so a reconnect can be matched back to a hand. */
        this.controllerHandByUID = new Map();
        /**
         * Pickups that lost their controller (e.g. a hand-tracking/controller switch that wasn't merged into the same
         * UID) but couldn't be dropped at that moment. Kept here so a controller reconnecting for the same hand can
         * reclaim them instead of leaving them stuck on a dead controller UID.
         */
        this.orphanedPickupsByHand = new Map();
        this.isXR = false;
        this.dropOffService = new DropOffService(this.connection);
    }
    /// Life-cycle methods ///
    onExitCurrentRoom(_flying) {
        this.dropEverything();
    }
    onRoomLoaded(_roomUID, previousRoomUID) {
        if (previousRoomUID === undefined)
            return;
        this.dropEverything();
    }
    onPresentationModeChanged(presentationMode, _viewPosition, _viewDirection) {
        this.isXR = presentationMode === PresentationMode.VR;
    }
    /// Pickup and drop off handlers ///
    handlePickupModelMessage(msg, askId) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j;
            let model = modelPool.getModel(msg.uid);
            if (model === undefined) {
                this.sendErrorAnswer(askId, `Can't find model for uid "${msg.uid}"`);
                return;
            }
            // Verify controller //
            let controllerUID = msg.params.controllerUid;
            const status = this.verifyController(controllerUID, model, (_a = msg.params.allowQuickReplacement) !== null && _a !== void 0 ? _a : false);
            if (!status.ok) {
                this.sendErrorAnswer(askId, status.err);
                return;
            }
            controllerUID = status.controllerUID;
            const controllerMetaData = status.meta;
            // If the controller already holds a model, then we drop it first //
            const prevPickup = this.controllerPickups.get(controllerUID);
            if (prevPickup !== undefined) {
                this.dropFromController(prevPickup, true);
            }
            // Create clone //
            if (msg.params.createTemporaryModel === true) {
                const originalModelName = model.getName();
                try {
                    model = this.clonePickupModel(model, controllerUID);
                    if (model === undefined) {
                        this.sendErrorAnswer(askId, `Cloning model "${originalModelName}" for pickup failed`);
                        return;
                    }
                }
                catch (e) {
                    this.sendErrorAnswer(askId, String(e));
                    return;
                }
                this.activateScriptsForPickupModel(model);
                modelPool.flushMessages();
            }
            // We use this uid since the model might have been cloned and thus msg.uid and the model uid can diverge
            const modelUID = model.getUID();
            // Pickup info creation //
            // If the picked up model is attached to another model then we need to detach it first
            let pickup = this.pickedUpModels.get(modelUID);
            if (pickup !== undefined) {
                this.dropFromController(pickup, true);
            }
            pickup = {
                modelUID,
                controllerUID,
                originalModelUID: msg.uid,
                keepSelectionActiveInVR: (_b = msg.params.keepSelectionActiveInVR) !== null && _b !== void 0 ? _b : false,
                // Movement
                offset: this.matrixFromOffset(msg.params.offset),
                // Drop off
                createTemporaryModel: (_c = msg.params.createTemporaryModel) !== null && _c !== void 0 ? _c : false,
                deleteOnDropOff: (_d = msg.params.deleteOnDropOff) !== null && _d !== void 0 ? _d : ((_e = msg.params.createTemporaryModel) !== null && _e !== void 0 ? _e : false),
                dropOffMode: msg.params.dropOffMode,
                dropOffLayers: msg.params.dropOffLayers,
                dropOffButton: (_f = msg.params.dropOffButton) !== null && _f !== void 0 ? _f : ButtonType.DISCARD,
                dropOnReleaseButton: (_g = msg.params.dropOnReleaseButton) !== null && _g !== void 0 ? _g : false,
                showDropOffIcon: (_h = msg.params.showDropOffIcon) !== null && _h !== void 0 ? _h : true,
                initialOrientation: this.getOrRegisterInitialOrientation(modelUID, model.getWorldTransform()),
                // Information before pickup
                previousParent: (_j = model.getParent()) === null || _j === void 0 ? void 0 : _j.getUID(),
                previousWorldTransform: model.getWorldTransform()
            };
            controllerMetaData.lastPickup = new Date();
            const canPickup = yield this.attachToController(pickup);
            if (!canPickup) {
                this.sendErrorAnswer(askId, `Cannot take ownership of model ${model.getName()}`);
                return;
            }
            // Force an immediate drop-off re-evaluation if pickup starts while already inside a drop-off area.
            this.dropOffService.refreshPickup(pickup);
            // Make sure that the model is also deleted if the user disconnects beforehand
            if (pickup.createTemporaryModel) {
                const localClientIdx = coreUserService.getLocalUser().clientIdx;
                const disconnectFacet = new DeleteModelOnDisconnectFacet(localClientIdx);
                model.setFacet(disconnectFacet);
                const createMessage = new CreateSubModelMessage(modelUID);
                createMessage.path = modelToAbsolutePath(model);
                serverConnection.queueBusMessage(createMessage);
                serverConnection.queueBusMessage(new UpdateFacetOnModelMessage(modelUID, disconnectFacet));
            }
            if (askId !== undefined)
                this.connection.answer(askId, new PickupModelResponseMessage(modelUID));
        });
    }
    handleDropModelMessage(msg) {
        return __awaiter(this, void 0, void 0, function* () {
            const pickup = this.pickedUpModels.get(msg.uid);
            if (pickup === undefined)
                return;
            this.dropFromController(pickup);
        });
    }
    handleResetModelPickupsRequestMessage(msg, askId) {
        for (const uid of msg.modelUids) {
            const pickup = this.pickedUpModels.get(uid);
            if (pickup !== undefined)
                this.dropFromController(Object.assign(Object.assign({}, pickup), { deleteOnDropOff: false }), true);
            for (const [hand, orphanedPickup] of this.orphanedPickupsByHand) {
                if (orphanedPickup.modelUID === uid)
                    this.orphanedPickupsByHand.delete(hand);
            }
        }
        this.connection.answer(askId, new AskOkMessage());
    }
    handleDropFromControllerMessage(msg) {
        return __awaiter(this, void 0, void 0, function* () {
            const pickup = this.controllerPickups.get(msg.uid);
            if (pickup === undefined)
                return;
            this.dropFromController(pickup);
        });
    }
    handleRequestCanPickupModelMessage(msg, askId) {
        if (this.pickedUpModels.has(msg.uid)) {
            this.connection.answer(askId, new AskErrorMessage(`Model ${msg.uid} already picked up`));
            return;
        }
        if (msg.controllerUid === undefined) {
            this.connection.answer(askId, new AskErrorMessage(`[pickup] Controller UID not specified`));
            return;
        }
        if (this.controllerPickups.has(msg.controllerUid)) {
            this.connection.answer(askId, new AskErrorMessage(`[pickup] Controller ${getModelNameByUID(msg.controllerUid)} already holds model`));
            return;
        }
        this.connection.answer(askId, new AskOkMessage());
    }
    /// Button handler ///
    handleControllerButtonMessage(msg) {
        const pickup = this.controllerPickups.get(msg.uid);
        if (pickup === undefined)
            return;
        const shouldDropOnConfiguredButton = msg.buttonType === pickup.dropOffButton && msg.input === ButtonSignal.CLICKED;
        const shouldDropOnReleaseButton = pickup.dropOnReleaseButton === true
            && msg.buttonType === pickup.dropOffButton
            && msg.input === ButtonSignal.RELEASED;
        if (!shouldDropOnConfiguredButton && !shouldDropOnReleaseButton)
            return;
        this.dropFromController(pickup);
    }
    /// Model handlers ///
    handleRemoveSubModelMessage(msg) {
        this.unregisterPickupAndEnableController(msg.uid);
    }
    handleDeleteModelMessage(msg) {
        this.unregisterPickupAndEnableController(msg.uid);
    }
    handleControllerConnectedMessage(msg) {
        this.controllerHandByUID.set(msg.uid, msg.hand);
        const orphanedPickup = this.orphanedPickupsByHand.get(msg.hand);
        if (orphanedPickup === undefined)
            return;
        this.orphanedPickupsByHand.delete(msg.hand);
        this.migratePickupToController(orphanedPickup, msg.uid);
    }
    handleControllerDisconnectedMessage(msg) {
        const pickup = this.controllerPickups.get(msg.uid);
        if (pickup === undefined)
            return;
        if (this.dropFromController(pickup))
            return;
        // Couldn't be dropped right now (e.g. not currently over a valid drop-off target). Remember which hand held
        // it so a controller reconnecting for that hand - e.g. switching from hand-tracking to a physical controller
        // - can reclaim it, instead of leaving it stuck on this now-dead controller UID forever.
        const hand = ControllerDisconnectedMessage.is(msg) ? this.controllerHandByUID.get(msg.uid) : undefined;
        if (hand !== undefined) {
            this.orphanedPickupsByHand.set(hand, pickup);
        }
    }
    /// Controller pickup and drop off methods ///
    attachToController(pickup) {
        return __awaiter(this, void 0, void 0, function* () {
            const messages = [];
            this.registerPickup(pickup);
            this.disableControllerSelection(pickup, messages);
            const pickupTransform = this.computeLocalTransform(pickup.modelUID, pickup.controllerUID, pickup.offset);
            const attachmentTargetUid = this.resolveAttachmentTargetUid(pickup.controllerUID);
            // When picking up a model it is linked to the controller's pickup anchor. For VR this is the
            // grip-space child, while the public pickup messages still refer to the real controller UID.
            const userIdx = coreUserService.getLocalUser().clientIdx;
            const linkToModelFacet = new LinkToModelFacet(attachmentTargetUid, pickupTransform === null || pickupTransform === void 0 ? void 0 : pickupTransform.toArray());
            const disconnectFacet = new RemoveFacetsOnDisconnectFacet([linkToModelFacet], userIdx);
            // Attach model to controller
            messages.push(new RemoveFacetFromModelMessage(pickup.modelUID, AttachToModelFacet), new UpdateFacetOnModelMessage(pickup.modelUID, linkToModelFacet), new UpdateFacetOnModelMessage(pickup.modelUID, disconnectFacet), new OnPickupModelMessage(pickup.modelUID, pickup.controllerUID, pickup.originalModelUID), new AttachmentChangedMessage(pickup.modelUID, undefined, pickup.controllerUID));
            messages.push(new BroadcastToPeerMessage([
                new OnPickupModelMessage(pickup.modelUID, pickup.controllerUID, pickup.originalModelUID),
                new AttachmentChangedMessage(pickup.modelUID, undefined, pickup.controllerUID)
            ]));
            this.connection.sendMultiple(messages);
            // Re-sync preview/contact state after link messages were sent.
            this.dropOffService.refreshPickup(pickup);
            return yield this.takeOwnership(pickup);
        });
    }
    /**
     * @param force Detach the pickup even if it isn't currently over a valid drop-off target, falling back to its
     * previous position. Used when the controller/model slot is being deliberately reassigned (e.g. picking up a
     * new tool while already holding one) and leaving the old pickup half-attached would be a bug, not just an
     * unwanted drop.
     */
    dropFromController(pickup, force = false) {
        // Check if picked up object can be dropped here
        const dropOffTarget = this.dropOffService.getValidDropOffTarget(pickup);
        const canDrop = dropOffTarget !== undefined && dropOffTarget.canBeDropped;
        if (!canDrop && !force)
            return false;
        this.releaseOwnership(pickup);
        const messages = [];
        this.enableControllerSelection(pickup, messages);
        this.updateAttachment(pickup, canDrop ? dropOffTarget : undefined, pickup.deleteOnDropOff, messages);
        // Handle model
        if (pickup.deleteOnDropOff) {
            serverConnection.queueBusMessage(new DeleteModelMessage(pickup.modelUID));
            modelPool.destroyModel(pickup.modelUID, {
                handleChildren: "destroyRecursive",
                destroyPermanently: true,
                onlyGenerateMessages: true,
                externalMessages: messages
            });
        }
        const peerMessages = messages.filter(msg => OnDropModelMessage.is(msg) ||
            AttachmentChangedMessage.is(msg) ||
            DeleteModelMessage.is(msg));
        if (peerMessages.length > 0) {
            messages.push(new BroadcastToPeerMessage(peerMessages));
        }
        if (messages.length > 0) {
            this.connection.sendMultiple(messages);
        }
        this.unregisterPickupAndEnableController(pickup.modelUID);
        // Remember when we last dropped something from this controller
        const controllerMetaData = this.getOrCreateControllerMetaData(pickup.controllerUID);
        controllerMetaData.lastDropOff = new Date();
        return true;
    }
    /**
     * Re-attaches an orphaned pickup (see handleControllerDisconnectedMessage) to a controller that just
     * reconnected for the same hand. Mirrors attachToController()'s link-facet setup, but skips re-registering
     * ownership/pickup bookkeeping that's still valid, since this is a continuation of the same hold, not a new
     * pickup.
     */
    migratePickupToController(pickup, newControllerUID) {
        const oldControllerUID = pickup.controllerUID;
        // Drop bookkeeping tied to the old, now-dead controller UID first (while pickup.controllerUID still points at
        // it) - this also hides its now-orphaned drop-off icon - before re-registering everything under the new UID.
        this.controllerPickups.delete(oldControllerUID);
        this.dropOffService.removePickup(pickup);
        pickup.controllerUID = newControllerUID;
        this.controllerPickups.set(newControllerUID, pickup);
        this.dropOffService.addPickup(pickup);
        const messages = [];
        this.disableControllerSelection(pickup, messages);
        const pickupTransform = this.computeLocalTransform(pickup.modelUID, newControllerUID, pickup.offset);
        const attachmentTargetUid = this.resolveAttachmentTargetUid(newControllerUID);
        const userIdx = coreUserService.getLocalUser().clientIdx;
        const linkToModelFacet = new LinkToModelFacet(attachmentTargetUid, pickupTransform === null || pickupTransform === void 0 ? void 0 : pickupTransform.toArray());
        const disconnectFacet = new RemoveFacetsOnDisconnectFacet([linkToModelFacet], userIdx);
        messages.push(new UpdateFacetOnModelMessage(pickup.modelUID, linkToModelFacet), new UpdateFacetOnModelMessage(pickup.modelUID, disconnectFacet), new AttachmentChangedMessage(pickup.modelUID, oldControllerUID, newControllerUID));
        messages.push(new BroadcastToPeerMessage([
            new AttachmentChangedMessage(pickup.modelUID, oldControllerUID, newControllerUID)
        ]));
        this.connection.sendMultiple(messages);
        // Re-run drop-off checks for the new controller immediately after orphan migration.
        this.dropOffService.refreshPickup(pickup);
    }
    /// Controller helper methods ///
    disableControllerSelection(pickup, messages) {
        const enableControllerSelection = this.isXR ? pickup.keepSelectionActiveInVR : true;
        const showControllerSelectionHelpers = false;
        messages.push(new EnableControllerSelectionMessage(enableControllerSelection, pickup.controllerUID));
        messages.push(new EnableModelSelectionMessage(pickup.modelUID, false));
        messages.push(new ControllerSelectionRayVisibilityMessage(showControllerSelectionHelpers, pickup.controllerUID));
        messages.push(new EnableMarkerRadarMessage(showControllerSelectionHelpers));
    }
    enableControllerSelection(pickup, messages) {
        messages.push(new EnableControllerSelectionMessage(true, pickup.controllerUID));
        messages.push(new EnableModelSelectionMessage(pickup.modelUID, true));
        messages.push(new EnableMarkerRadarMessage(true));
    }
    /// Ownership helper methods ///
    takeOwnership(pickup) {
        return __awaiter(this, void 0, void 0, function* () {
            networkEntitiesService.createNetworkEntity(pickup.modelUID); // ToDo: is it important that we create the network facet?
            const isMine = yield networkEntitiesService.takeOwnership(pickup.modelUID, false);
            // return isNetworked && isMine
            return isMine;
        });
    }
    releaseOwnership(pickup) {
        networkEntitiesService.surrenderOwnership(pickup.modelUID);
        networkEntitiesService.removeNetworkEntity(pickup.modelUID);
    }
    /// Pickup helper methods ///
    /**
     * Returns the stored initial orientation for a model, or extracts and stores it from the given
     * world transform if this is the first time the model is being picked up.
     */
    getOrRegisterInitialOrientation(modelUID, worldTransform) {
        const existing = this.initialOrientationByModelUID.get(modelUID);
        if (existing !== undefined)
            return existing;
        const orientation = new Quaternion();
        worldTransform.decompose(new Vector3(), orientation, new Vector3());
        this.initialOrientationByModelUID.set(modelUID, orientation);
        return orientation;
    }
    registerPickup(pickup) {
        this.controllerPickups.set(pickup.controllerUID, pickup);
        this.pickedUpModels.set(pickup.modelUID, pickup);
        this.dropOffService.addPickup(pickup);
    }
    unregisterPickupAndEnableController(modelUID) {
        const pickup = this.pickedUpModels.get(modelUID);
        if (pickup === undefined)
            return;
        this.controllerPickups.delete(pickup.controllerUID);
        this.pickedUpModels.delete(pickup.modelUID);
        const messages = [];
        this.enableControllerSelection(pickup, messages);
        messages.push(new OnDropModelMessage(modelUID, pickup.controllerUID));
        this.connection.sendMultiple(messages);
        this.dropOffService.removePickup(pickup);
    }
    /// Attachment helper methods ///
    updateAttachment(pickup, dropOffTarget, deleteModel, messages) {
        var _a, _b, _c, _d;
        // A forced drop (e.g. replacing what's on a controller with a newly picked up tool) might not have a valid
        // drop-off target. Fall back to the previous position rather than attaching to an incomplete/empty target.
        if (dropOffTarget === undefined) {
            this.attachToPreviousPosition(pickup, deleteModel, messages);
            return;
        }
        switch (pickup.dropOffMode) {
            case DropOffMode.DROP_OFF_AREA:
                if (dropOffTarget.targetUID !== undefined) {
                    this.createAttachmentMessages(pickup.modelUID, pickup.controllerUID, dropOffTarget.targetUID, dropOffTarget.worldTransform, deleteModel, messages);
                }
                else {
                    this.createAttachmentMessages(pickup.modelUID, pickup.controllerUID, dropOffTarget.dropOffAreaUID, dropOffTarget.worldTransform, deleteModel, messages);
                }
                break;
            case DropOffMode.ALWAYS: // & DROP_OFF_AREA without drop off target
                {
                    const childWorldTransform = (_b = (_a = modelPool.getModel(pickup.modelUID)) === null || _a === void 0 ? void 0 : _a.getWorldTransform()) !== null && _b !== void 0 ? _b : new Matrix4();
                    const newTarget = (_d = (_c = modelPool.getModel(pickup.modelUID)) === null || _c === void 0 ? void 0 : _c.getRoom()) !== null && _d !== void 0 ? _d : modelPool.getCurrentRoom();
                    const localTransform = newTarget !== undefined
                        ? worldToLocalTransform(childWorldTransform, newTarget.getWorldTransform(), TEMP_MAT_1)
                        : childWorldTransform; // If we have no parent, then we assume that world coordinates == local coordinates
                    this.createAttachmentMessages(pickup.modelUID, pickup.controllerUID, newTarget === null || newTarget === void 0 ? void 0 : newTarget.getUID(), localTransform, deleteModel, messages);
                }
                break;
            case DropOffMode.PREVIOUS_POSITION:
                this.attachToPreviousPosition(pickup, deleteModel, messages);
                break;
        }
    }
    attachToPreviousPosition(pickup, deleteModel, messages) {
        var _a;
        const newTarget = modelPool.getModel((_a = pickup.previousParent) !== null && _a !== void 0 ? _a : INVALID_UID);
        const localTransform = newTarget !== undefined
            ? worldToLocalTransform(pickup.previousWorldTransform, newTarget.getWorldTransform(), TEMP_MAT_1)
            : pickup.previousWorldTransform; // If we have no parent, then we assume that world coordinates == local coordinates
        if (newTarget === undefined)
            console.warn("[pickup] Previous parent doesn't exist anymore.");
        this.createAttachmentMessages(pickup.modelUID, pickup.controllerUID, newTarget === null || newTarget === void 0 ? void 0 : newTarget.getUID(), localTransform, deleteModel, messages);
    }
    createAttachmentMessages(modelUID, sourceUID, targetUID, localTransform, deleteModel, messages) {
        const userIdx = coreUserService.getLocalUser().clientIdx;
        const params = { offsetMatrix: localTransform.toArray() };
        const attachToModelFacet = new AttachToModelFacet(targetUID, params);
        const disconnectFacet = new RemoveFacetsOnDisconnectFacet([attachToModelFacet], userIdx);
        const target = targetUID !== undefined ? modelPool.getModelOrThrow(targetUID) : undefined;
        const worldTransform = target !== undefined
            ? target.getWorldTransform().clone().multiply(localTransform)
            : localTransform;
        messages.push(new RemoveFacetFromModelMessage(modelUID, LinkToModelFacet));
        // We only need to attach the model if it won't be deleted
        if (!deleteModel) {
            messages.push(new UpdateFacetOnModelMessage(modelUID, attachToModelFacet));
            // Peers may have a different target world transform, so also synchronize the final world-space pose.
            messages.push(new BroadcastToPeerMessage([
                new UpdateWorldTransformationMessage(modelUID, worldTransform.toArray())
            ]));
        }
        messages.push(new UpdateFacetOnModelMessage(modelUID, disconnectFacet), new OnDropModelMessage(modelUID), new OnDropModelMessage(modelUID, sourceUID), new AttachmentChangedMessage(modelUID, sourceUID, targetUID));
    }
    /// Math helper methods ///
    /**
     * The model might already have a localTransformFacet. For now, we only want to preserve position and orientation, while
     * the positional offset comes from localTransform. In case it becomes important to also keep the local positional
     * offset, then we might need to find another solution to compute the local transform
     */
    computeLocalTransform(uid, _controllerUID, pickupLocalTransform) {
        const model = modelPool.getModel(uid);
        const localTransformFacet = model === null || model === void 0 ? void 0 : model.getFacet(LocalTransformFacet);
        if (localTransformFacet === undefined)
            return pickupLocalTransform;
        const modelLocalTransform = new Matrix4().compose(new Vector3(), // we are not interested in the old positional offset
        new Quaternion(), new Vector3().fromArray(localTransformFacet.scale));
        pickupLocalTransform = pickupLocalTransform !== null && pickupLocalTransform !== void 0 ? pickupLocalTransform : new Matrix4();
        return pickupLocalTransform.clone().multiply(modelLocalTransform);
    }
    resolveAttachmentTargetUid(controllerUid) {
        if (!this.isXR || controllerUid === controllerDeviceService.mouseDeviceUID) {
            return controllerUid;
        }
        return getControllerPickupTargetUid(controllerUid);
    }
    matrixFromOffset(offset) {
        if (offset === undefined)
            return undefined;
        const position = new Vector3().fromArray(offset.position);
        const orientation = new Quaternion().fromArray(offset.orientation);
        orientation.normalize(); // Make sure to normalize the quaternion, because the user might have entered an invalid quaternion
        const scale = new Vector3().fromArray(offset.scale);
        const matrix = new Matrix4();
        matrix.compose(position, orientation, scale);
        return matrix;
    }
    /// Drop helper methods ///
    dropEverything() {
        const pickupToDrop = [...this.pickedUpModels.values()];
        for (const pickup of pickupToDrop) {
            this.dropFromController(pickup);
        }
        // Don't let a stale orphan record reattach to a controller after everything was just force-dropped.
        this.orphanedPickupsByHand.clear();
    }
    /// Controller meta data helper methods ///
    getOrCreateControllerMetaData(controllerUID) {
        let controllerMetaData = this.controllerMetaDataMap.get(controllerUID);
        if (controllerMetaData === undefined) {
            controllerMetaData = {};
            this.controllerMetaDataMap.set(controllerUID, controllerMetaData);
        }
        return controllerMetaData;
    }
    /// Clone helper methods ///
    clonePickupModel(model, controllerUID) {
        var _a, _b;
        const originalParent = model.getParent();
        if (originalParent === undefined) {
            console.error(`[pickup] Model ${model.getName()} has no parent!`);
            return undefined;
        }
        const cloneParent = (_b = (_a = model.getRoom()) !== null && _a !== void 0 ? _a : modelPool.getCurrentRoom()) !== null && _b !== void 0 ? _b : originalParent;
        const originalWorldTransform = model.getWorldTransform();
        const cloneName = `pickup_${model.getName()}_${controllerUID}`;
        // Temporary pickup clones must live under a shared parent so peers can create the hierarchy, too.
        const clone = modelPool.cloneModel(model, {
            parent: cloneParent,
            name: cloneName,
            peer: true,
            allowOverwritingModel: true,
        });
        const cloneLocalTransform = worldToLocalTransform(originalWorldTransform, cloneParent.getWorldTransform(), TEMP_MAT_1);
        clone.setTransform(cloneLocalTransform, { peer: true });
        clone.flushMessages();
        return clone;
    }
    activateScriptsForPickupModel(model) {
        model.traverse(currentModel => {
            var _a, _b;
            const scriptFacet = (_a = currentModel.getFacet(ScriptFacet)) === null || _a === void 0 ? void 0 : _a.clone();
            if (scriptFacet !== undefined && !scriptFacet.active) {
                scriptFacet.active = true;
                currentModel.setFacet(scriptFacet);
            }
            const scriptComposeFacet = (_b = currentModel.getFacet(ScriptComposeFacet)) === null || _b === void 0 ? void 0 : _b.clone();
            if (scriptComposeFacet === undefined)
                return;
            let wasChanged = false;
            for (const script of scriptComposeFacet.scripts) {
                if (script.active)
                    continue;
                script.active = true;
                wasChanged = true;
            }
            if (wasChanged) {
                currentModel.setFacet(scriptComposeFacet);
            }
        });
    }
    /// Verification helper methods ///
    verifyController(controllerUID, model, allowQuickReplacement) {
        if (controllerUID === undefined)
            return { ok: false, err: "Controller UID was not specified in message" };
        // We don't allow quick drop off and pickup within a short span
        const controllerMetaData = this.getOrCreateControllerMetaData(controllerUID !== null && controllerUID !== void 0 ? controllerUID : -1);
        if (controllerMetaData.lastDropOff !== undefined) {
            const timeDifferenceMs = new Date().getTime() - controllerMetaData.lastDropOff.getTime();
            if (timeDifferenceMs < 500 && !allowQuickReplacement)
                return { ok: false, err: `Cannot pickup the model "${model.getName()}" from the controller "${controllerUID}" shortly after dropping it off` };
        }
        return { ok: true, controllerUID, meta: controllerMetaData };
    }
    /// Answer message helper methods ///
    sendErrorAnswer(askId, errorMessage) {
        if (askId !== undefined) {
            console.warn(`[pickup] ${errorMessage}`);
            this.connection.answer(askId, new AskErrorMessage(errorMessage));
        }
    }
}
__decorate([
    MessageRequestHandler(PickupModelMessage)
], ModelPickupModule.prototype, "handlePickupModelMessage", null);
__decorate([
    MessageHandler(DropModelMessage)
], ModelPickupModule.prototype, "handleDropModelMessage", null);
__decorate([
    MessageRequestHandler(ResetModelPickupsRequestMessage)
], ModelPickupModule.prototype, "handleResetModelPickupsRequestMessage", null);
__decorate([
    MessageHandler(DropFromControllerMessage)
], ModelPickupModule.prototype, "handleDropFromControllerMessage", null);
__decorate([
    MessageRequestHandler(RequestCanPickupModelMessage)
], ModelPickupModule.prototype, "handleRequestCanPickupModelMessage", null);
__decorate([
    MessageHandler(ControllerButtonMessage)
], ModelPickupModule.prototype, "handleControllerButtonMessage", null);
__decorate([
    MessageHandler(RemoveSubModelMessage)
], ModelPickupModule.prototype, "handleRemoveSubModelMessage", null);
__decorate([
    MessageHandler(DeleteModelMessage)
], ModelPickupModule.prototype, "handleDeleteModelMessage", null);
__decorate([
    MessageHandler(ControllerConnectedMessage)
], ModelPickupModule.prototype, "handleControllerConnectedMessage", null);
__decorate([
    MessageHandler(ControllerDisconnectedMessage, MouseDisconnectedMessage)
], ModelPickupModule.prototype, "handleControllerDisconnectedMessage", null);
